import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import { T } from "@repo/transifex";
import { DialogLoading, DialogPayment } from "~/components";
import { useAppContext } from "~/providers/app";
import { useOpenCashDrawer } from "~/hooks";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { toast } from "~/components/hooks/use-toast";
import { useDebounce } from "~/hooks/use-debounce";
import { useFreedomPayOrderStatus } from "~/api/orders/hooks/use-create-order";
import { DisplayErrorMessage } from "~/components/display-error-message";
import { useCancelPoiPayment } from "~/hooks/freedompay/use-cancel-poi-payment";
import {
  useOrder,
  useOrderRefund,
  usePrintOrderReceipt
} from "~/hooks/queries/orders";
import { useUserProductsPos } from "~/api/products/hooks/use-user-products";
import { useVoidPoiPayment } from "~/hooks/freedompay/use-void-poi-payment";
import { logger } from "~/services/logger";
import { isDisplayError, DisplayErrorCodeEnum } from "~/utils/display-error";

import { CardPaymentDialog } from "./card-payment-dialog";
import { VerifySignature } from "./verify-signature";
import { type CardPaymentProps } from "./card-payment.types";
import { TerminalReadyIcon } from "./TerminalReadyIcon";

export const FreedomPayCardPayment = ({
  orderId,
  totalPriceFormatted,
  isSplitPaymentActive
}: CardPaymentProps) => {
  const [isSignatureAccepted, setIsSignatureAccepted] = useState(false);

  const navigate = useNavigate();

  const openCashDrawer = useOpenCashDrawer();

  const { data: till } = useCurrentTill();
  const { data: order, status: orderStatus } = useOrder(orderId);
  const { data: userProducts } = useUserProductsPos();
  const { status, payload } = useFreedomPayOrderStatus();
  const {
    hardware: { printer = {} }
  } = useAppContext();

  const { mutate: createReceipt } = usePrintOrderReceipt();
  const { mutate: cancelPoiPayment, status: cancelPoiPaymentStatus } =
    useCancelPoiPayment();
  const { mutate: voidPoiPayment, status: voidPoiPaymentStatus } =
    useVoidPoiPayment();
  const { mutate: refundOrder, status: refundStatus } = useOrderRefund();

  useEffect(() => {
    if (status === "error" && payload?.error) {
      logger.error(
        payload.error,
        "FreedomPay: Payment error screen displayed",
        {
          order_id: orderId,
          is_timeout:
            isDisplayError(payload.error) &&
            payload.error.code === DisplayErrorCodeEnum.POI_TIMEOUT,
          event: "freedompay_error_screen_displayed"
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- payload changes when status changes, no need to track separately
  }, [status]);

  // The checkout mutations exist before this screen mounts, so `idle` means
  // they were garbage collected under an unattended error: nothing is in flight.
  useEffect(() => {
    if (status === "idle") {
      navigate("/pos/sell", { replace: true });
    }
  }, [status, navigate]);

  const terminalId = till?.freedompayTerminalId ?? undefined;

  if (!till?.id) {
    throw new Error("Till ID is required");
  }

  const handleCancel = useDebounce(() => {
    if (!terminalId) {
      toast({
        title: <T _str="No terminal found" />,
        description: <T _str="The payment cannot be cancelled" />,
        variant: "destructive"
      });
      return;
    }

    if (status !== "pending-poi-payment") {
      return;
    }

    if (isSplitPaymentActive) {
      openCashDrawer("automatic");
    }

    cancelPoiPayment(undefined, {
      onSuccess: () => {
        navigate("/pos/sell", { replace: true });
        toast({
          title: <T _str="Payment cancelled" />,
          description: <T _str="The payment has been cancelled" />,
          variant: "success"
        });
      },
      onError: (error) => {
        logger.error(error, "FreedomPay: Error cancelling POI payment");
        toast({
          title: <T _str="Error cancelling payment" />,
          description: (
            <T _str="An error occurred while cancelling the payment" />
          ),
          variant: "destructive"
        });
      }
    });
  }, 500);

  const handlePrintReceipt = () => {
    if (!printer) {
      toast({
        title: <T _str="Printer not connected" />,
        description: (
          <T _str="Please connect the printer to print the receipt" />
        ),
        variant: "destructive"
      });
      return;
    }

    if (status !== "pending-signature-check") {
      return;
    }

    if (!orderId) {
      return;
    }

    createReceipt({
      orderId,
      userId: userProducts?.user?.id ?? null,
      receiptList: payload.receiptList
    });
  };

  const handleAcceptSignature = () => {
    if (status !== "pending-signature-check") {
      return;
    }
    toast({
      title: <T _str="Payment accepted" />,
      description: <T _str="The payment has been accepted" />,
      variant: "success"
    });
    setIsSignatureAccepted(true);
  };

  const handleRejectSignature = () => {
    if (status !== "pending-signature-check") {
      return;
    }

    const cardPaymentId = order?.payments?.find(
      (payment) => payment.paymentProvider === "card"
    )?.id;

    if (!cardPaymentId || !orderId) {
      logger.warn(
        "FreedomPay: Payment ID not found on reject signature verification",
        {
          order_id: orderId,
          payments: order?.payments
        }
      );
      toast({
        title: <T _str="Payment not found" />,
        description: <T _str="The payment cannot be cancelled" />,
        variant: "destructive"
      });
      return;
    }

    logger.debug("FreedomPay: signature rejected, refunding then voiding", {
      order_id: orderId,
      payment_id: cardPaymentId,
      request_id: payload.requestId,
      event: "freedompay_signature_reject_start"
    });

    refundOrder(
      {
        orderId,
        refundMethod: "card",
        shouldGenerateServiceId: false
      },
      {
        onSuccess: () => {
          logger.debug(
            "FreedomPay: signature-reject void MRC (reused sale MRC)",
            {
              order_id: orderId,
              payment_id: cardPaymentId,
              request_id: payload.requestId,
              event: "freedompay_signature_reject_void_mrc"
            }
          );
          voidPoiPayment(
            {
              orderId,
              requestId: payload.requestId,
              paymentId: cardPaymentId
            },
            {
              onSuccess: () => {
                toast({
                  title: <T _str="Payment cancelled" />,
                  description: <T _str="The payment has been cancelled" />,
                  variant: "success"
                });
                navigate("/pos/sell", { replace: true });
              },
              onError: (error) => {
                logger.error(
                  error,
                  "Error voiding payment due to signature verification",
                  {
                    order_id: orderId,
                    payment_id: cardPaymentId,
                    request_id: payload.requestId
                  }
                );
                toast({
                  title: (
                    <T _str="Something went wrong while cancelling the payment" />
                  ),
                  description: <T _str="Please try again later" />,
                  variant: "destructive"
                });
              }
            }
          );
        },
        onError: (error) => {
          logger.error(error, "FreedomPay: Error cancelling order", {
            order_id: orderId,
            payment_id: cardPaymentId
          });
          toast({
            title: (
              <T _str="Something went wrong while cancelling the payment" />
            ),
            description: <T _str="Please try again later" />,
            variant: "destructive"
          });
        }
      }
    );
  };

  // Render based on payment status
  if (status === "success" && orderId) {
    return (
      <DialogPayment
        orderId={orderId}
        status="success"
        receiptList={payload.receiptList}
      />
    );
  }

  if (status === "error") {
    return (
      <DialogPayment status="error">
        <DisplayErrorMessage error={payload.error} />
      </DialogPayment>
    );
  }

  if (
    status === "pending-signature-check" &&
    till.type === "attended" &&
    orderId
  ) {
    if (isSignatureAccepted) {
      return (
        <DialogPayment
          status="success"
          orderId={orderId}
          receiptList={payload.receiptList}
        />
      );
    }

    if (
      orderStatus === "pending" ||
      voidPoiPaymentStatus === "pending" ||
      refundStatus === "pending"
    ) {
      return <DialogLoading />;
    }

    return (
      <VerifySignature
        onAcceptSignature={handleAcceptSignature}
        onRejectSignature={handleRejectSignature}
        onPrintReceipt={handlePrintReceipt}
      />
    );
  }

  return (
    <CardPaymentDialog
      title={<T _str="Awaiting payment on terminal" />}
      description={totalPriceFormatted}
      icon={<TerminalReadyIcon />}
    >
      <CardPaymentDialog.CancelButton
        disabled={!terminalId}
        loading={
          cancelPoiPaymentStatus === "pending" ||
          status === "pending-order-creation"
        }
        onClick={handleCancel}
        variant="danger"
      />
    </CardPaymentDialog>
  );
};
